/**
 * Dashboard onboarding checklist — steps, routes, rewards, and tutorial video IDs.
 *
 * Guide clips are separate from Szkoła Ofertatora (videoRegistry).
 * Optional `videoLocales` gates which UI languages see the clip (omit = all).
 */

import type { Locale } from '../i18n/locales'
import { isContentLocaleAllowed } from '../lib/contentLocale'

export type OnboardingStepKey =
  | 'create_account'
  | 'connect_allegro'
  | 'descriptions_flow'
  | 'titles_flow'
  | 'grafikomat_flow'
  | 'parameters_flow'
  | 'copy_offer'
  | 'create_bundle'

export type OnboardingStepRole = 'core' | 'optional' | 'challenge'

export interface OnboardingStepDef {
  key: OnboardingStepKey
  route: string | null
  /** YouTube video id for the onboarding guide step (not Szkoła Ofertatora) */
  videoId?: string
  /** Locales where videoId is shown. Omit = all locales. */
  videoLocales?: readonly Locale[]
  relatedModuleId?: string
  order: number
  /** Endowed progress — always completed, not navigable */
  endowed?: boolean
  /** Wyróżnij krok jako rekomendowany start po Allegro */
  recommended?: boolean
}

/** Locale-aware video id; undefined when missing or not allowed for `locale`. */
export function resolveOnboardingVideoId(
  step: OnboardingStepDef,
  locale: string,
): string | undefined {
  if (!step.videoId) return undefined
  if (!isContentLocaleAllowed(step.videoLocales, locale)) return undefined
  return step.videoId
}

/** Kroki nie dają punktów — nagroda tylko za ukończenie przewodnika podstawowego. */
export const ONBOARDING_STEP_REWARD_POINTS: Record<OnboardingStepKey, number> = {
  create_account: 0,
  connect_allegro: 0,
  descriptions_flow: 0,
  grafikomat_flow: 0,
  parameters_flow: 0,
  titles_flow: 0,
  copy_offer: 0,
  create_bundle: 0,
}

/** Nagroda za ukończenie przewodnika podstawowego (+ pełny dostęp do modułów). */
export const ONBOARDING_COMPLETION_BONUS = 30

/** Szacunkowa wartość punktów w różnych modułach (do komunikatu produktowego). */
export function estimateOnboardingProductValue(points: number) {
  const safe = Math.max(points, 1)
  return {
    descriptions: Math.max(1, Math.floor(safe / 3)),
    parameters: Math.max(1, Math.floor(safe / 2)),
    titles: Math.max(1, Math.floor(safe / 2)),
  }
}

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  { key: 'create_account', route: null, order: 0, endowed: true },
  {
    key: 'connect_allegro',
    route: '/accounts',
    videoId: 'fQAOSH-hT5E',
    videoLocales: ['pl'],
    relatedModuleId: 'konta_marketplace',
    order: 1,
  },
  {
    key: 'descriptions_flow',
    route: '/allegro/offer-management',
    videoId: 'uF8niVT6JDQ',
    videoLocales: ['pl'],
    relatedModuleId: 'allegro_opisomat',
    order: 2,
    recommended: true,
  },
  {
    key: 'titles_flow',
    route: '/allegro/offer-management',
    videoId: 'U5yugy8TW2s',
    videoLocales: ['pl'],
    relatedModuleId: 'allegro_tytuly_v2',
    order: 3,
  },
  {
    key: 'grafikomat_flow',
    route: '/allegro/offer-management',
    videoId: 'GvY_0XDmURg',
    videoLocales: ['pl'],
    relatedModuleId: 'allegro_grafikomat',
    order: 4,
  },
  {
    key: 'parameters_flow',
    route: '/allegro/offer-management',
    videoId: 'n-JbeS4wS00',
    videoLocales: ['pl'],
    relatedModuleId: 'allegro_parametry',
    order: 5,
  },
  {
    key: 'create_bundle',
    route: '/allegro/flexible-bundles',
    order: 6,
  },
  {
    key: 'copy_offer',
    route: '/allegro/offer-management',
    order: 7,
  },
]

/** @deprecated use ONBOARDING_COMPLETION_BONUS */
export const ONBOARDING_REWARD_POINTS = ONBOARDING_COMPLETION_BONUS
